'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import { RotateCcwIcon } from 'lucide-react';
import { useStopwatch } from 'react-timer-hook';
import { useLocalStorage } from 'react-use';

import { GameScore } from '~/app/games/_components/game-score';
import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { MatchGameVersions } from '~/types/games';
import { shuffleArray } from '~/utils/array';
import { clamp } from '~/utils/number';

import resultsImage from '../_assets/match-dialog-image.svg';
import { GameHighScore } from '../../_components/game-high-score';
import GameResultDialog from '../../_components/game-result-dialog';
import { GameTimer } from '../../_components/game-timer';
import { MatchGameBoardSkeleton } from './match-game-board';

const MatchGameBoard = dynamic(() => import('./match-game-board').then((mod) => mod.MatchGameBoard), {
  ssr: false,
  loading: () => <MatchGameBoardSkeleton />,
});

const cardSymbolSets = {
  default: ['boat', 'rocket', 'duck', 'bear', 'ball', 'cupcake', 'control', 'guitar', 'lollipop'],
  princess: [
    'pumpkin',
    'flower',
    'witches',
    'book',
    'bow',
    'frog',
    'leaves',
    'bird',
    'teapot',
    'shell',
    'magic-lamp',
    'egg',
  ],
};

function createGameState(version: MatchGameVersions) {
  const cardIds = shuffleArray([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  const cardSymbols = shuffleArray([...cardSymbolSets[version]]);

  return { cardIds, cardSymbols };
}

const cardCount = 12;
const movesPerGame = cardCount / 2;
const secondsPerMove = 2.5;
const secondsPerGame = movesPerGame * secondsPerMove;
const timeScoreWeight = 0.3;

interface MatchGameProps {
  version?: MatchGameVersions;
}

export default function MatchGame({ version = 'default' }: MatchGameProps) {
  const storageKeyPrefix = `${version === 'default' ? '' : `${version}-`}`;

  const [gameState, setGameState] = React.useState(() => createGameState(version));
  const [gameKey, setGameKey] = React.useState(0);
  const [moves, setMoves] = React.useState<number>(0);
  const [finished, setFinished] = React.useState<boolean>(false);
  const [newHighScore, setNewHighScore] = React.useState<boolean>(false);
  const [resultsModalOpen, setResultsModalOpen] = React.useState<boolean>(false);

  const [bestScore, setBestScore] = useLocalStorage<number>(`${storageKeyPrefix}match-game-best-score`);
  const [bestMoves, setBestMoves] = useLocalStorage<number>(`${storageKeyPrefix}match-game-best-moves`);
  const [bestTime, setBestTime] = useLocalStorage<number>(`${storageKeyPrefix}match-game-best-time`);

  const { seconds, minutes, pause, reset } = useStopwatch({ autoStart: true });
  const totalSeconds = React.useMemo(() => Math.floor(minutes * 60 + seconds), [seconds, minutes]);

  const timeScore = React.useMemo(() => Math.floor(totalSeconds - secondsPerGame), [totalSeconds]);
  const movesScore = React.useMemo(() => moves - movesPerGame, [moves]);
  const totalScore = React.useMemo(
    () => clamp(0, Math.floor(100 - (timeScore * timeScoreWeight + movesScore)), 100),
    [timeScore, movesScore],
  );

  const restartGame = React.useCallback(() => {
    setGameState(createGameState(version));
    setGameKey((key) => key + 1);
    setMoves(0);
    setFinished(false);
    setNewHighScore(false);
    setResultsModalOpen(false);
    reset();
  }, [reset, version]);

  const finishGame = React.useCallback(() => {
    pause();

    const bests = {
      score: bestScore ?? 0,
      moves: bestMoves ?? Number.MAX_SAFE_INTEGER,
      time: bestTime ?? Number.MAX_SAFE_INTEGER,
    };

    // Update best score, tie-break on fewest moves
    if (
      totalScore > bests.score ||
      (totalScore === bests.score && moves < bests.moves) ||
      (totalScore === bests.score && moves === bests.moves && totalSeconds < bests.time)
    ) {
      setBestMoves(moves);
      setBestTime(totalSeconds);
      setBestScore(totalScore);
      setNewHighScore(true);
    }

    setFinished(true);
    setTimeout(() => setResultsModalOpen(true), 1000);
  }, [bestMoves, bestScore, bestTime, moves, pause, setBestMoves, setBestScore, setBestTime, totalScore, totalSeconds]);

  return (
    <div className='flex flex-col items-center px-8'>
      <MatchGameBoard
        key={gameKey}
        cardIds={gameState.cardIds}
        cardSymbols={gameState.cardSymbols}
        finishGame={finishGame}
        setMoves={setMoves}
      />
      <GameTimer moves={moves} seconds={totalSeconds} />

      <GameResultDialog
        title={newHighScore ? (totalScore === 100 ? 'A Perfect Score!' : 'New Best Score!') : 'Awesome Job!'}
        headerImage={resultsImage}
        open={resultsModalOpen}
        setOpen={setResultsModalOpen}
        showConfetti={resultsModalOpen && finished}
        confettiAmount={newHighScore || totalScore === 100 ? 'excessive' : 'default'}
      >
        <GameScore moves={moves} score={totalScore} seconds={totalSeconds} />
        {bestMoves != null && bestScore != null && bestTime != null && (
          <GameHighScore moves={bestMoves} score={bestScore} seconds={bestTime} />
        )}

        <div className='mt-6 flex flex-col gap-3'>
          <Button variant='default' onClick={restartGame}>
            <RotateCcwIcon />
            Play Again
          </Button>
          <Button variant='outline-secondary' asChild>
            <Link href='/games'>Back to Games</Link>
          </Button>
        </div>
      </GameResultDialog>
    </div>
  );
}
